/**
 * Gem Power threshold bonuses (PLAN §11 "Gem sockets"): 3 colours × thresholds 120/240/360/480.
 *  - Ruby: offense. Sapphire: defense and utility. Emerald: sustain and healing.
 * Every bonus is a proc or a passive aura (data only). They are class-generic (`classId: null`),
 * and their auras are identical for every class, which the shared sim registry requires (see
 * items.trinkets.ts on `everyBind`).
 */
import type { GemColor, GemPowerBonus } from './types';
import { everyBind, hiddenAura, marker, timedBuff } from './items.trinkets';

/** Gem Power needed for each bonus rank of a colour (one rank per 120 Gem Power). */
export const GEM_POWER_PER_BONUS_RANK = 120;
export const GEM_BONUS_RANKS = 4;
export const GEM_THRESHOLD_LIST: readonly number[] = Array.from({ length: GEM_BONUS_RANKS }, (_, i) => GEM_POWER_PER_BONUS_RANK * (i + 1));

const bonus = (color: GemColor, threshold: number, name: string, description: string, effect: GemPowerBonus['effect']): GemPowerBonus => ({
  id: `${color}.${threshold}`,
  color,
  threshold,
  classId: null,
  name,
  description,
  effect,
});

export const GEM_BONUSES: readonly GemPowerBonus[] = [
  // ---- Ruby: offense -----------------------------------------------------------
  bonus('ruby', 120, 'Kindled Focus', 'Your critical strikes reduce the cooldown of your Q ability by 0.5 s (0.5 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.ruby.120', 'Kindled Focus', {
        procs: [{ on: 'critDealt', icdMs: 500, effects: everyBind('Q').map((abilityId) => ({ type: 'modifyCooldown' as const, to: 'self' as const, abilityId, deltaMs: -500 })) }],
      }),
    },
  ]),
  bonus('ruby', 240, 'Ember Surge', 'Your damage has an 8% chance to sear the target for extra fire damage (1 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.ruby.240', 'Ember Surge', {
        procs: [{ on: 'damageDealt', chance: 0.08, icdMs: 1000, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.9 } }] }],
      }),
    },
  ]),
  bonus('ruby', 360, 'Blazing Tempo', 'Using your Q ability grants +15% haste for 10 s.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.ruby.360', 'Blazing Tempo', {
        procs: [
          {
            on: 'cast',
            abilityIds: everyBind('Q'),
            effects: [{ type: 'applyAura', to: 'self', aura: timedBuff('gem.ruby.360.buff', 'Blazing Tempo', 10000, { mods: [{ stat: 'hastePct', value: 0.15 }] }) }],
          },
        ],
      }),
    },
  ]),
  bonus('ruby', 480, 'Heart of the Inferno', 'Critical strikes grant +2% damage done for 8 s, stacking up to 5 times (1 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.ruby.480', 'Heart of the Inferno', {
        procs: [
          {
            on: 'critDealt',
            icdMs: 1000,
            effects: [
              {
                type: 'applyAura',
                to: 'self',
                aura: { id: 'gem.ruby.480.buff', name: 'Inferno', kind: 'buff', durationMs: 8000, stacking: 'stack', maxStacks: 5, mods: [{ stat: 'damageDonePct', value: 0.02 }] },
              },
            ],
          },
        ],
      }),
    },
  ]),

  // ---- Sapphire: defense and utility -------------------------------------------
  bonus('sapphire', 120, 'Tidal Ward', 'Taking damage has a 10% chance to shield you for 3% of your max HP (6 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.sapphire.120', 'Tidal Ward', {
        procs: [{ on: 'damageTaken', chance: 0.1, icdMs: 6000, effects: [{ type: 'absorb', to: 'self', auraId: 'gem.sapphire.120.shield', amount: { pctSourceMaxHp: 0.03 } }] }],
      }),
    },
    { op: 'registerAura', aura: { id: 'gem.sapphire.120.shield', name: 'Tidal Ward', kind: 'buff', durationMs: 8000, stacking: 'refresh', absorb: {} } },
  ]),
  bonus('sapphire', 240, 'Flowing Current', 'Your E and R abilities recover 20% faster.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.sapphire.240', 'Flowing Current', { mods: [{ stat: 'cooldownRatePct', value: 0.2, abilityIds: [...everyBind('E'), ...everyBind('R')] }] }),
    },
  ]),
  bonus('sapphire', 360, 'Frost Bulwark', 'Taking damage below 35% health shields you for 15% of your max HP. 60 s cooldown.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.sapphire.360', 'Frost Bulwark', {
        procs: [
          {
            on: 'damageTaken',
            effects: [
              {
                type: 'conditional',
                cond: { type: 'and', all: [{ type: 'selfHpBelow', pct: 0.35 }, { type: 'selfLacksAura', auraId: 'gem.sapphire.360.cd' }] },
                then: [
                  { type: 'absorb', to: 'self', auraId: 'gem.sapphire.360.shield', amount: { pctSourceMaxHp: 0.15 } },
                  { type: 'applyAura', to: 'self', aura: marker('gem.sapphire.360.cd', 'Frost Bulwark', 60000) },
                ],
              },
            ],
          },
        ],
      }),
    },
    { op: 'registerAura', aura: { id: 'gem.sapphire.360.shield', name: 'Frost Bulwark', kind: 'buff', durationMs: 10000, stacking: 'refresh', absorb: {} } },
  ]),
  bonus('sapphire', 480, 'Glacial Aegis', 'You take 6% less damage, and your F ability recovers 25% faster.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.sapphire.480', 'Glacial Aegis', {
        mods: [
          { stat: 'damageTakenPct', value: -0.06 },
          { stat: 'cooldownRatePct', value: 0.25, abilityIds: everyBind('F') },
        ],
      }),
    },
  ]),

  // ---- Emerald: sustain and healing -------------------------------------------
  bonus('emerald', 120, 'Verdant Echo', 'Your heals have a 15% chance to heal the target again for a small amount (1 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.emerald.120', 'Verdant Echo', {
        procs: [{ on: 'healDealt', chance: 0.15, icdMs: 1000, effects: [{ type: 'heal', amount: { coef: 0.8 } }] }],
      }),
    },
  ]),
  bonus('emerald', 240, 'Lifebloom', 'Your damage has a 10% chance to heal you for 1.5% of your max HP (2 s internal cooldown).', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.emerald.240', 'Lifebloom', {
        procs: [{ on: 'damageDealt', chance: 0.1, icdMs: 2000, effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.015 } }] }],
      }),
    },
  ]),
  bonus('emerald', 360, 'Renewal', 'You receive 8% more healing and deal 5% more healing.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.emerald.360', 'Renewal', {
        mods: [
          { stat: 'healingTakenPct', value: 0.08 },
          { stat: 'healingDonePct', value: 0.05 },
        ],
      }),
    },
  ]),
  bonus('emerald', 480, 'Wellspring', 'Taking damage below 40% health heals you for 20% of your max HP. 90 s cooldown.', [
    {
      op: 'addPassive',
      aura: hiddenAura('gem.emerald.480', 'Wellspring', {
        procs: [
          {
            on: 'damageTaken',
            effects: [
              {
                type: 'conditional',
                cond: { type: 'and', all: [{ type: 'selfHpBelow', pct: 0.4 }, { type: 'selfLacksAura', auraId: 'gem.emerald.480.cd' }] },
                then: [
                  { type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.2 } },
                  { type: 'applyAura', to: 'self', aura: marker('gem.emerald.480.cd', 'Wellspring', 90000) },
                ],
              },
            ],
          },
        ],
      }),
    },
  ]),
];

/** The bonus table, grouped by colour in threshold order (UI). */
export function gemBonusLadder(color: GemColor): GemPowerBonus[] {
  return GEM_BONUSES.filter((b) => b.color === color).sort((a, b) => a.threshold - b.threshold);
}

